import React from "react"
import { Linking, Pressable, View } from "react-native"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { Body, Small } from "@/components/standard/BFText"
import type { MilestoneTaskType } from "@/generated/graphql"
import Clock from "phosphor-react-native/src/icons/Clock"
import ArrowUpRight from "phosphor-react-native/src/icons/ArrowUpRight"
import CaretRight from "phosphor-react-native/src/icons/CaretRight"
import { type Href, router } from "expo-router"
import { BFColors } from "@/constants/Colors"
import FolderSimple from "phosphor-react-native/src/icons/FolderSimple"
import { Separator } from "@/components/reusables/ui/separator"
import EmailIcon from "phosphor-react-native/src/icons/Envelope"

interface RequestItemProps {
  task: MilestoneTaskType
  onPress?: () => void
  status: "todo" | "completed" | "up_next"
  showSeparator?: boolean
}

const formatEstimatedTime = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (remainingMinutes === 0) {
    return `${hours}h`
  }
  return `${hours}h ${remainingMinutes}m`
}

const isInAppUrl = (url: string): boolean => {
  return url.startsWith("in_app:")
}

const getInAppPath = (url: string): string => {
  if (isInAppUrl(url)) {
    return url.replace("in_app:", "")
  }
  return ""
}

const isFileUploadTask = (taskType: string): boolean => {
  return taskType === "file_upload"
}

export const RequestItem: React.FC<RequestItemProps> = ({
  task,
  onPress,
  status,
  showSeparator = true,
}) => {
  const isCompleted = status === "completed"
  const isDisabled = status === "up_next"
  const hasTaskUrl = !!task.taskUrl
  const isFileUpload = isFileUploadTask(task.taskType || "")
  const isInApp = isInAppUrl(task.taskType || "")

  // Determine if item should be clickable
  const isClickable = isInApp || hasTaskUrl

  const handlePress = () => {
    if (isDisabled) return

    if (isInApp) {
      const path = getInAppPath(task.taskType || "")
      router.push(`./${path}` as Href)
      return
    }

    if (hasTaskUrl) {
      void Linking.openURL(task.taskUrl!).catch((error) => {
        console.error("Failed to open URL:", error)
      })
      return
    }

    if (onPress) {
      onPress()
    }
  }

  const getActionIcon = () => {
    if (isDisabled || isCompleted) return null

    if (task.taskType === "email") {
      return <EmailIcon size={16} color={BFColors.midnightBlue} />
    }

    if (isFileUpload) {
      return <FolderSimple size={16} color={BFColors.midnightBlue} />
    }

    if (isInApp) {
      return <CaretRight size={16} color={BFColors.midnightBlue} />
    }

    if (hasTaskUrl) {
      return <ArrowUpRight size={16} color={BFColors.midnightBlue} />
    }

    return null
  }

  const content = (
    <Vertical className="">
      <Horizontal className="items-center justify-between">
        <Horizontal className="items-start flex-1 pr-3">
          <View className="flex-1">
            <Body
              color={isCompleted ? "hint" : isDisabled ? "disabled" : "default"}
              className="mb-1"
            >
              {task.name}
            </Body>

            <Horizontal className="items-center gap-1">
              <Clock size={14} color="#676080" />
              <Small color="hint">
                {formatEstimatedTime(task.estimatedTime || 0)}
                {task.description ? ` | ${task.description}` : null}
              </Small>

              {/* <> */}
              {/*  <View className="size-1 bg-muted-foreground rounded-full" /> */}
              {/*  <Small color="hint"> */}
              {/*    {task.dealOrIndividual === "deal" */}
              {/*      ? "Deal specific" */}
              {/*      : "individual"} */}
              {/*  </Small> */}
              {/* </> */}

              {isFileUpload && (
                <>
                  <View className="size-1 bg-muted-foreground rounded-full" />
                  <Small color="hint">Upload required</Small>
                </>
              )}
            </Horizontal>
          </View>
        </Horizontal>

        {getActionIcon()}
      </Horizontal>

      {showSeparator && <Separator className={"my-4"} />}
    </Vertical>
  )

  if (isClickable) {
    return (
      <Pressable onPress={handlePress} testID={"task-" + task.id}>
        {content}
      </Pressable>
    )
  }

  return content
}
